import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Sparkles, Shield, UserCheck, ChevronRight } from 'lucide-react';
import api from '../../api/client';

export default function ConsentModal({ open, onConsentComplete, onLogout }) {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!checked) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.post('users/consent/', { policy_version: '1.0' });
      if (response.data.success || response.status === 200) {
        localStorage.removeItem('requires_consent');
        onConsentComplete();
      } else {
        throw new Error('Failed to record consent');
      }
    } catch (err) {
      console.error('Consent error:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while saving your consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      PaperProps={{
        style: {
          borderRadius: '20px',
          padding: '8px',
          maxWidth: '550px',
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            borderRadius: '16px',
            p: 3,
            color: 'white',
            textAlign: 'center',
            mb: 3,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <Sparkles size={40} style={{ marginBottom: '12px', opacity: 0.9 }} />
          <Typography variant="h5" fontWeight="700" sx={{ mb: 1, color: '#ffffff' }}>
            Updates & Platform Consent
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffffff', opacity: 0.85, maxWidth: '360px', mx: 'auto' }}>
            We've upgraded Aptiview with advanced intelligence. Review and acknowledge the policy changes below to proceed.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'rgba(99, 102, 241, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6366f1',
                flexShrink: 0,
              }}
            >
              <Sparkles size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                AI-Powered Code Diagnostics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Experience enhanced automated coding evaluations and real-time skill matching algorithms.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2.5 }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'rgba(168, 85, 247, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#a855f7',
                flexShrink: 0,
              }}
            >
              <Shield size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                Activity Logs & Usage Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                To improve platform reliability and performance, administrators will periodically review user activity and usage metrics.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'rgba(16, 185, 129, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#10b981',
                flexShrink: 0,
              }}
            >
              <UserCheck size={20} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                Transparency & Compliance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your explicit consent is logged with timestamp, IP address, and browser metadata to ensure security and compliance auditing.
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Box
            sx={{
              p: 1.5,
              mb: 2.5,
              borderRadius: '8px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#ef4444',
            }}
          >
            <Typography variant="caption">{error}</Typography>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                sx={{
                  color: '#6366f1',
                  '&.Mui-checked': {
                    color: '#6366f1',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.primary" fontWeight="500">
                I acknowledge and accept the updated terms and policies
              </Typography>
            }
            sx={{ mb: 3, display: 'flex', alignItems: 'center' }}
          />

          <Button
            fullWidth
            variant="contained"
            disabled={!checked || loading}
            onClick={handleSubmit}
            sx={{
              background: checked
                ? 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'
                : '#e2e8f0',
              color: checked ? 'white' : '#94a3b8',
              borderRadius: '12px',
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: '600',
              textTransform: 'none',
              boxShadow: checked ? '0 10px 20px rgba(99, 102, 241, 0.25)' : 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                boxShadow: '0 12px 24px rgba(99, 102, 241, 0.35)',
              },
              '&.Mui-disabled': {
                background: '#f1f5f9',
                color: '#94a3b8',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Proceed to Dashboard</span>
                <ChevronRight size={18} />
              </Box>
            )}
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={onLogout}
            sx={{
              mt: 1.5,
              color: '#64748b',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '0.875rem',
              '&:hover': {
                color: '#ef4444',
                background: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            Cancel and Log Out
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
